import React, { createContext, useContext, useState, useEffect } from 'react';
import { getCurrentUser } from '../services/authService';
import { addXP } from '../utils/questUtils';

export interface User {
  id: string;
  email: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  isNewUser: boolean;
  hasCompletedOnboarding: boolean;
  login: (token: string, isNewUser?: boolean) => void;
  logout: () => void;
  fetchUser: () => Promise<void>;
  completeOnboarding: () => void;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  user: null,
  isNewUser: false,
  hasCompletedOnboarding: true,
  login: () => {},
  logout: () => {},
  fetchUser: async () => {},
  completeOnboarding: () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [isNewUser, setIsNewUser] = useState<boolean>(false);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState<boolean>(true);

  const fetchUser = async () => {
    try {
      const userData = await getCurrentUser();
      if (userData) {
        setUser(userData);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setIsAuthenticated(true);
      fetchUser();
      // Check and update daily usage for Budget Pro achievement
      checkAndUpdateDailyUsage();
      // Auto-start yearly income quest if not completed
      checkAndAutoStartYearlyIncomeQuest();

      // Check onboarding status
      const onboardingCompleted = localStorage.getItem('onboarding_completed');
      setHasCompletedOnboarding(onboardingCompleted === 'true');
    }
  }, []);

  const checkAndUpdateDailyUsage = () => {
    const today = new Date().toDateString(); // Format: "Mon Dec 18 2024"
    const usageDatesStr = localStorage.getItem('daily_usage_dates');
    const budgetProUnlocked = localStorage.getItem('budget_pro_unlocked');

    let usageDates: string[] = [];
    if (usageDatesStr) {
      try {
        usageDates = JSON.parse(usageDatesStr);
      } catch (e) {
        usageDates = [];
      }
    }

    // If already used today, don't update
    if (usageDates.includes(today)) {
      return;
    }

    // Add today's usage
    usageDates.push(today);

    // Keep only the last 40 days to prevent unlimited growth
    if (usageDates.length > 40) {
      usageDates = usageDates.slice(-40);
    }

    localStorage.setItem('daily_usage_dates', JSON.stringify(usageDates));

    // Check for 30 days of usage (not necessarily consecutive)
    if (!budgetProUnlocked && usageDates.length >= 30) {
      // Unlock Budget Pro achievement
      localStorage.setItem('budget_pro_unlocked', 'true');
      addXP(100); // Award 100 XP for Budget Pro achievement

      // Show achievement notification
      setTimeout(() => {
        alert('🧮 Achievement Unlocked: Budget Pro! You earned 100 XP for using the app for 30 days!');
      }, 1000);
    }
  };

  const checkAndAutoStartYearlyIncomeQuest = () => {
    // Check if yearly income quest has been completed
    const yearlyIncomeQuestState = localStorage.getItem('quest_yearly-income');

    if (yearlyIncomeQuestState) {
      try {
        const questState = JSON.parse(yearlyIncomeQuestState);
        // If quest is completed (timeRemaining: 0 and isStarted: false), don't auto-start
        if (questState.timeRemaining === 0 && !questState.isStarted) {
          return; // Quest already completed
        }
      } catch (error) {
        console.error('Error parsing yearly income quest state:', error);
      }
    }

    // If we reach here, the quest hasn't been completed, so auto-start it
    const startedState = {
      isStarted: true,
      timeRemaining: 0 // This quest doesn't use a timer
    };
    localStorage.setItem('quest_yearly-income', JSON.stringify(startedState));
    console.log('[AuthContext] Auto-started yearly income quest on sign-in');
  };

  const clearUserData = () => {
    const keysToRemove = [
      'user_xp',
      'budget_income',
      'budget_frequency',
      'budget_pro_unlocked',
      'streak_starter_unlocked',
      'account_master_unlocked',
      'daily_usage_dates',
      'login_dates',
      'category_matcher_results',
      'emergency_fund_goal_amount',
      'userLives'
    ];
    
    // Remove quest-related keys (they start with 'quest_')
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('quest_')) {
        localStorage.removeItem(key);
      }
    });
    
    // Remove specific user data keys
    keysToRemove.forEach(key => localStorage.removeItem(key));
  };

  const login = (token: string, isNewUser: boolean = false) => {
    // Set new user state
    setIsNewUser(isNewUser);
    
    // Clear all user data if this is a new user
    if (isNewUser) {
      clearUserData();
      // Ensure new users see the goal modal by removing any existing userGoal
      localStorage.removeItem('userGoal');
      // New users haven't completed onboarding
      localStorage.removeItem('onboarding_completed');
      setHasCompletedOnboarding(false);
    } else {
      // Check onboarding status for existing users
      const onboardingCompleted = localStorage.getItem('onboarding_completed');
      setHasCompletedOnboarding(onboardingCompleted === 'true');
    }
    
    localStorage.setItem('token', token);
    setIsAuthenticated(true);
    fetchUser();
    
    // Initialize XP to 0 for new users
    if (isNewUser) {
      localStorage.setItem('user_xp', '0');
    } else {
      addXP(10); // Award 10 XP for login (not for new registration)
    }
    
    // Check and update login streak for Streak Starter achievement
    checkAndUpdateLoginStreak();

    // Check and update daily usage for Budget Pro achievement
    checkAndUpdateDailyUsage();

    // Auto-start yearly income quest if not completed (for returning users)
    if (!isNewUser) {
      checkAndAutoStartYearlyIncomeQuest();
    }
  };

  const checkAndUpdateLoginStreak = () => {
    const today = new Date().toDateString(); // Format: "Mon Dec 18 2024"
    const loginDatesStr = localStorage.getItem('login_dates');
    const streakStarterUnlocked = localStorage.getItem('streak_starter_unlocked');
    
    let loginDates: string[] = [];
    if (loginDatesStr) {
      try {
        loginDates = JSON.parse(loginDatesStr);
      } catch (e) {
        loginDates = [];
      }
    }
    
    // If already logged in today, don't update
    if (loginDates.includes(today)) {
      return;
    }
    
    // Add today's login
    loginDates.push(today);
    
    // Keep only the last 10 days to prevent unlimited growth
    if (loginDates.length > 10) {
      loginDates = loginDates.slice(-10);
    }
    
    localStorage.setItem('login_dates', JSON.stringify(loginDates));
    
    // Check for 3-day consecutive streak
    if (!streakStarterUnlocked && hasThreeDayStreak(loginDates)) {
      // Unlock Streak Starter achievement
      localStorage.setItem('streak_starter_unlocked', 'true');
      addXP(100); // Award 100 XP for Streak Starter achievement
      
      // Show achievement notification
      setTimeout(() => {
        alert('🔥 Achievement Unlocked: Streak Starter! You earned 100 XP for logging in 3 days in a row!');
      }, 1000);
    }
  };

  const hasThreeDayStreak = (loginDates: string[]): boolean => {
    if (loginDates.length < 3) return false;
    
    // Sort dates to ensure proper order
    const sortedDates = loginDates
      .map(dateStr => new Date(dateStr))
      .sort((a, b) => a.getTime() - b.getTime());
    
    // Check for any 3 consecutive days
    for (let i = 0; i <= sortedDates.length - 3; i++) {
      const date1 = sortedDates[i];
      const date2 = sortedDates[i + 1];
      const date3 = sortedDates[i + 2];
      
      const day1 = Math.floor(date1.getTime() / (1000 * 60 * 60 * 24));
      const day2 = Math.floor(date2.getTime() / (1000 * 60 * 60 * 24));
      const day3 = Math.floor(date3.getTime() / (1000 * 60 * 60 * 24));
      
      if (day2 === day1 + 1 && day3 === day2 + 1) {
        return true; // Found 3 consecutive days
      }
    }
    
    return false;
  };

  const logout = () => {
    // Clear all user data including token
    clearUserData();
    localStorage.removeItem('token');
    localStorage.removeItem('onboarding_completed');
    setIsAuthenticated(false);
    setUser(null);
    setIsNewUser(false);
    setHasCompletedOnboarding(true);
  };

  const completeOnboarding = () => {
    localStorage.setItem('onboarding_completed', 'true');
    setHasCompletedOnboarding(true);
    setIsNewUser(false);
  };

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      user, 
      isNewUser, 
      hasCompletedOnboarding, 
      login, 
      logout, 
      fetchUser, 
      completeOnboarding 
    }}>
      {children}
    </AuthContext.Provider>
  );
}; 